import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { ChevronDown } from "lucide-react";

export const Route = createFileRoute("/_authenticated/admin/calculadora")({
  head: () => ({
    meta: [
      { title: "Calculadora de plan de pagos ZUBA | Admin" },
      {
        name: "description",
        content:
          "Simulador interno de planes de pago en USD y guaraníes para unidades ZUBA.",
      },
      { name: "robots", content: "noindex, nofollow" },
    ],
    links: [
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,500;9..144,700&family=IBM+Plex+Mono:wght@400;500;600&display=swap",
      },
    ],
  }),
  component: CalculadoraPage,
});

const usdFmt = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const pygFmt = new Intl.NumberFormat("es-PY", { maximumFractionDigits: 0 });

const fmtUsd = (n: number) => `US$ ${usdFmt.format(Number.isFinite(n) ? n : 0)}`;
const fmtPyg = (n: number) => `Gs. ${pygFmt.format(Number.isFinite(n) ? Math.round(n) : 0)}`;

const num = (v: string) => {
  const n = parseFloat(v.replace(",", "."));
  return Number.isFinite(n) ? n : 0;
};

function Field({
  label,
  value,
  onChange,
  suffix,
  step = "any",
  min = 0,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  suffix?: string;
  step?: string;
  min?: number;
}) {
  return (
    <label className="block">
      <span className="block text-[11px] font-semibold uppercase tracking-[0.14em] text-[#8a6a1f]">
        {label}
      </span>
      <div className="mt-1.5 flex items-center rounded-md border border-[#d9cdb4] bg-white/80 focus-within:border-[#c99a1e] focus-within:ring-2 focus-within:ring-[#e5c25c]/40">
        <input
          type="number"
          inputMode="decimal"
          step={step}
          min={min}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-transparent px-3 py-2 font-mono text-[15px] text-[#173a3f] outline-none [font-family:'IBM_Plex_Mono',ui-monospace,monospace]"
        />
        {suffix ? (
          <span className="pr-3 text-xs font-medium text-[#7d8b8c]">{suffix}</span>
        ) : null}
      </div>
    </label>
  );
}

function Line({
  label,
  hint,
  usd,
  pyg,
  tone = "default",
}: {
  label: string;
  hint?: string;
  usd: number;
  pyg: number;
  tone?: "default" | "cost" | "total";
}) {
  const color =
    tone === "cost" ? "text-[#b1541f]" : tone === "total" ? "text-[#123338]" : "text-[#173a3f]";
  return (
    <div
      className={`flex items-start justify-between gap-4 border-b border-dashed border-[#cdbfa4] py-3 ${
        tone === "total" ? "border-b-0" : ""
      }`}
    >
      <div>
        <div
          className={`text-sm font-semibold ${
            tone === "cost" ? "text-[#b1541f]" : "text-[#173a3f]"
          } ${tone === "total" ? "text-base" : ""}`}
        >
          {label}
        </div>
        {hint ? <div className="text-xs text-[#7d8b8c]">{hint}</div> : null}
      </div>
      <div className="shrink-0 text-right [font-family:'IBM_Plex_Mono',ui-monospace,monospace]">
        <div className={`${tone === "total" ? "text-xl" : "text-[15px]"} font-semibold ${color}`}>
          {fmtUsd(usd)}
        </div>
        <div className={`text-xs ${tone === "cost" ? "text-[#b1541f]/80" : "text-[#5c6d6f]"}`}>
          {fmtPyg(pyg)}
        </div>
      </div>
    </div>
  );
}

function CalculadoraPage() {
  const [valor, setValor] = useState("100000");
  const [moneda, setMoneda] = useState<"USD" | "PYG">("USD");
  const [tc, setTc] = useState("6030");
  const [pctEntrega, setPctEntrega] = useState("30");
  const [pctSaldo, setPctSaldo] = useState("40");
  const [pctUltima, setPctUltima] = useState("30");
  const [cuotas, setCuotas] = useState("24");
  const [pctInteres, setPctInteres] = useState("10");
  const [openDetalle, setOpenDetalle] = useState(false);

  const r = useMemo(() => {
    const tipoCambio = num(tc);
    const ingresado = num(valor);
    const valorPropiedad =
      moneda === "USD" ? ingresado : tipoCambio > 0 ? ingresado / tipoCambio : 0;
    const pe = num(pctEntrega);
    const ps = num(pctSaldo);
    const pu = num(pctUltima);
    const n = Math.max(0, Math.floor(num(cuotas)));
    const pi = num(pctInteres);

    const montoEntregaInicial = valorPropiedad * (pe / 100);
    const montoSaldoLlave = valorPropiedad * (ps / 100);
    const montoUltimaCuota = valorPropiedad * (pu / 100);
    const montoInteres = montoSaldoLlave * (pi / 100);
    const saldoConInteres = montoSaldoLlave + montoInteres;
    const valorPorCuota = n > 0 ? saldoConInteres / n : 0;
    const totalAPagar = montoEntregaInicial + saldoConInteres + montoUltimaCuota;
    const sumaPct = pe + ps + pu;

    return {
      tipoCambio,
      valorPropiedad,
      n,
      pe,
      ps,
      pu,
      pi,
      montoEntregaInicial,
      montoSaldoLlave,
      montoUltimaCuota,
      montoInteres,
      saldoConInteres,
      valorPorCuota,
      totalAPagar,
      sumaPct,
    };
  }, [valor, moneda, tc, pctEntrega, pctSaldo, pctUltima, cuotas, pctInteres]);


  const g = (usd: number) => usd * r.tipoCambio;
  const pctOk = Math.abs(r.sumaPct - 100) < 0.001;

  return (
    <div className="min-h-full bg-[#faf5ea] -m-6 p-6 md:-m-8 md:p-8">
      <header className="mb-6">
        <h1 className="text-2xl font-bold text-[#123338] [font-family:'Fraunces',ui-serif,Georgia,serif] md:text-3xl">
          Calculadora de plan de pagos
        </h1>
        <p className="mt-1 text-sm text-[#5c6d6f]">
          Simulación interna en dólares y guaraníes para unidades ZUBA.
        </p>
      </header>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Formulario */}
        <section className="rounded-xl border border-[#e0d4bb] bg-[#fffdf7] p-5 shadow-sm">
          <h2 className="mb-4 text-sm font-semibold uppercase tracking-[0.16em] text-[#8a6a1f]">
            Datos de la operación
          </h2>
          <div className="mb-4">
            <span className="block text-[11px] font-semibold uppercase tracking-[0.14em] text-[#8a6a1f]">
              Moneda del valor ingresado
            </span>
            <div className="mt-1.5 inline-flex rounded-md border border-[#d9cdb4] bg-white/80 p-0.5">
              {(["USD", "PYG"] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => {
                    if (m === moneda) return;
                    const tcNum = num(tc);
                    const v = num(valor);
                    if (tcNum > 0 && v > 0) {
                      setValor(
                        m === "PYG" ? String(Math.round(v * tcNum)) : (v / tcNum).toFixed(2),
                      );
                    }
                    setMoneda(m);
                  }}
                  className={`rounded px-4 py-1.5 text-sm font-semibold transition ${
                    moneda === m
                      ? "bg-[#123338] text-[#e5c25c]"
                      : "text-[#5c6d6f] hover:text-[#173a3f]"
                  }`}
                >
                  {m === "USD" ? "Dólares (US$)" : "Guaraníes (Gs.)"}
                </button>
              ))}
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label="Valor de la propiedad"
              value={valor}
              onChange={setValor}
              suffix={moneda === "USD" ? "USD" : "Gs."}
            />
            <Field label="Tipo de cambio del día" value={tc} onChange={setTc} suffix="Gs./US$" />

            <Field label="Entrega inicial" value={pctEntrega} onChange={setPctEntrega} suffix="%" />
            <Field
              label="Saldo hasta entrega de llave"
              value={pctSaldo}
              onChange={setPctSaldo}
              suffix="%"
            />
            <Field label="Última cuota" value={pctUltima} onChange={setPctUltima} suffix="%" />
            <Field
              label="Cantidad de cuotas"
              value={cuotas}
              onChange={setCuotas}
              suffix="cuotas"
              step="1"
            />
            <Field
              label="Interés por financiamiento"
              value={pctInteres}
              onChange={setPctInteres}
              suffix="%"
            />
          </div>

          <div
            className={`mt-4 rounded-md border px-3 py-2 text-sm [font-family:'IBM_Plex_Mono',ui-monospace,monospace] ${
              pctOk
                ? "border-[#cfe0cf] bg-[#f1f7f0] text-[#3d6b46]"
                : "border-[#e6b3a0] bg-[#fdf0eb] text-[#b1541f]"
            }`}
          >
            Suma de porcentajes: {usdFmt.format(r.sumaPct)}%
            {pctOk ? " ✓" : " — debería sumar 100%"}
          </div>
        </section>

        {/* Resultado tipo recibo */}
        <section className="relative">
          <div className="overflow-hidden rounded-t-xl border border-[#e0d4bb] bg-[#fffdf7] shadow-sm">
            <div className="bg-[#123338] px-5 py-4">
              <div className="text-[11px] uppercase tracking-[0.2em] text-[#e5c25c]">
                Resumen del plan
              </div>
              <div className="text-lg font-bold text-white [font-family:'Fraunces',ui-serif,Georgia,serif]">
                Plan de pagos
              </div>
              <div className="mt-1 text-xs text-white/60 [font-family:'IBM_Plex_Mono',ui-monospace,monospace]">
                Propiedad: {fmtUsd(r.valorPropiedad)} · {fmtPyg(r.valorPropiedad * r.tipoCambio)} ·
                TC {pygFmt.format(r.tipoCambio)} Gs.
              </div>
            </div>

            <div className="px-5 py-2">
              <Line
                label="Entrega inicial"
                hint={`${usdFmt.format(r.pe)}% del valor`}
                usd={r.montoEntregaInicial}
                pyg={g(r.montoEntregaInicial)}
              />
              <Line
                label="Cuotas hasta entrega de llave"
                hint={`${r.n} cuota${r.n === 1 ? "" : "s"} · ${usdFmt.format(r.ps)}% financiado`}
                usd={r.valorPorCuota}
                pyg={g(r.valorPorCuota)}
              />
              <Line
                label="Interés por financiamiento"
                hint={`${usdFmt.format(r.pi)}% sobre ${fmtUsd(r.montoSaldoLlave)}`}
                usd={r.montoInteres}
                pyg={g(r.montoInteres)}
                tone="cost"
              />
              <Line
                label="Última cuota"
                hint={`${usdFmt.format(r.pu)}% del valor`}
                usd={r.montoUltimaCuota}
                pyg={g(r.montoUltimaCuota)}
              />
              <div className="mt-2 rounded-lg bg-[#f4ecd8] px-4 py-1">
                <Line
                  label="Total a pagar"
                  usd={r.totalAPagar}
                  pyg={g(r.totalAPagar)}
                  tone="total"
                />
              </div>

              <button
                type="button"
                onClick={() => setOpenDetalle((o) => !o)}
                className="mt-4 flex w-full items-center justify-between rounded-md border border-[#d9cdb4] px-3 py-2 text-sm font-medium text-[#173a3f] hover:bg-[#f4ecd8]"
              >
                Ver detalle de cada cuota
                <ChevronDown
                  className={`h-4 w-4 transition-transform ${openDetalle ? "rotate-180" : ""}`}
                />
              </button>

              {openDetalle ? (
                <div className="mt-3 overflow-x-auto">
                  <table className="w-full text-sm [font-family:'IBM_Plex_Mono',ui-monospace,monospace]">
                    <thead>
                      <tr className="border-b border-[#cdbfa4] text-left text-[11px] uppercase tracking-[0.12em] text-[#8a6a1f]">
                        <th className="py-2 pr-2 font-semibold">Concepto</th>
                        <th className="py-2 pr-2 text-right font-semibold">USD</th>
                        <th className="py-2 text-right font-semibold">Gs.</th>
                      </tr>
                    </thead>
                    <tbody className="text-[#173a3f]">
                      <tr className="border-b border-dashed border-[#ded2ba]">
                        <td className="py-1.5 pr-2">Entrega inicial</td>
                        <td className="py-1.5 pr-2 text-right">{fmtUsd(r.montoEntregaInicial)}</td>
                        <td className="py-1.5 text-right">{fmtPyg(g(r.montoEntregaInicial))}</td>
                      </tr>
                      {Array.from({ length: Math.min(r.n, 600) }, (_, i) => (
                        <tr key={i} className="border-b border-dashed border-[#ded2ba]">
                          <td className="py-1.5 pr-2">Cuota {i + 1}</td>
                          <td className="py-1.5 pr-2 text-right">{fmtUsd(r.valorPorCuota)}</td>
                          <td className="py-1.5 text-right">{fmtPyg(g(r.valorPorCuota))}</td>
                        </tr>
                      ))}
                      <tr>
                        <td className="py-1.5 pr-2 font-semibold">Última cuota</td>
                        <td className="py-1.5 pr-2 text-right font-semibold">
                          {fmtUsd(r.montoUltimaCuota)}
                        </td>
                        <td className="py-1.5 text-right font-semibold">
                          {fmtPyg(g(r.montoUltimaCuota))}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              ) : null}

              <p className="mt-5 mb-4 text-xs leading-relaxed text-[#7d8b8c]">
                Simulación referencial. No constituye una oferta vinculante ni un compromiso
                comercial. Verificá siempre el tipo de cambio del día antes de compartir este plan
                con el cliente.
              </p>
            </div>
          </div>
          {/* Borde perforado tipo recibo */}
          <div
            className="h-4 w-full"
            style={{
              backgroundImage:
                "radial-gradient(circle at 8px 0, transparent 8px, #fffdf7 8px)",
              backgroundSize: "16px 16px",
              backgroundRepeat: "repeat-x",
              filter: "drop-shadow(0 1px 0 #e0d4bb)",
            }}
          />
        </section>
      </div>
    </div>
  );
}
